import { makeAutoObservable } from 'mobx';
import { BiDirectionalMap } from '@/utils/Wap/BiDirectionalMap';
import { detectPlatform, type PlatformFlavor } from '@/utils/platform';
import type { IKeyMapModel } from '@/interfaces/models';

export enum KeyActions {
  JUMP_TO_COORDINATES = 'Jump to Coordinates',
  MOVE_UP = 'Move Up',
  MOVE_DOWN = 'Move Down',
  MOVE_LEFT = 'Move Left',
  MOVE_RIGHT = 'Move Right',
  CENTER = 'CENTER',
  ZOOM_IN = 'Zoom In',
  ZOOM_OUT = 'Zoom Out',
  ZOOM_RESET = 'Zoom Reset',
  PAN_TOOL = 'Pan Tool',
  SELECT_TOOL = 'Select Tool',
  TILE_TOOL = 'Tile Tool',
  CHIP_TOOL = 'Chip Tool',
  DELETE_TOOL = 'Delete Tool',
  DEBUG_TOOL = 'Debug Tool',
  UNDO = 'Undo',
  REDO = 'Redo',
  ROTATE = 'Rotate',
  ROTATE_BACKWARDS = 'Rotate Backwards',
  SETTINGS = 'Settings',
  COPY = 'Copy',
  PASTE = 'Paste',
  CUT = 'Cut',
  DELETE_SELECTION = 'Delete Selection',
  ESCAPE = 'Escape',
}

export const tileShortcutKeys = ['1', '2', '3', '4', '5', '6', '7', '8', '9'] as const;
export type TileShortcutKey = (typeof tileShortcutKeys)[number];
export interface TileShortcutTarget {
  /** Stable palette identity: a template ID or a namespaced chip:<id>. */
  templateId: string;
  name: string;
}

export function isTileShortcutKey(key: string): key is TileShortcutKey {
  return /^[1-9]$/.test(key);
}

export interface KeyCombination {
  key: string;
  primary?: boolean;
  alt?: boolean;
  shift?: boolean;
}

const SPECIAL_KEY_DISPLAY: Record<string, string> = {
  ArrowUp: '↑',
  ArrowDown: '↓',
  ArrowLeft: '←',
  ArrowRight: '→',
  ' ': 'Space',
  Enter: 'Return',
  Escape: 'Esc',
  Delete: 'Del',
  Backspace: '⌫',
  Tab: '⇥',
  Home: '↖',
  End: '↘',
  PageUp: '⇞',
  PageDown: '⇟',
};

const SHIFTED_KEY_MAP: Record<string, string> = {
  '~': '`',
  '!': '1',
  '@': '2',
  '#': '3',
  $: '4',
  '%': '5',
  '^': '6',
  '&': '7',
  '*': '8',
  '(': '9',
  ')': '0',
  _: '-',
  '+': '=',
  '{': '[',
  '}': ']',
  '|': '\\',
  ':': ';',
  '"': "'",
  '<': ',',
  '>': '.',
  '?': '/',
};

const REVERSE_SPECIAL_KEY_DISPLAY: Record<string, string> = Object.fromEntries(
  Object.entries(SPECIAL_KEY_DISPLAY).map(([original, display]) => [display, original]),
);

function normalizeKey(key: string, shift: boolean): string {
  if (shift) {
    const shifted = SHIFTED_KEY_MAP[key];
    if (shifted) return shifted;
  }

  if (key.length === 1) {
    return key.toLowerCase();
  }

  return key;
}

export function normalizeCombination(combo: KeyCombination): KeyCombination {
  const shift = !!combo.shift;
  const normalized: KeyCombination = {
    key: normalizeKey(combo.key, shift),
  };

  if (combo.primary) {
    normalized.primary = true;
  }

  if (combo.alt) {
    normalized.alt = true;
  }

  if (shift) {
    normalized.shift = true;
  }

  return normalized;
}

function getKeyDisplayName(combo: KeyCombination): string {
  const { key } = combo;

  const special = SPECIAL_KEY_DISPLAY[key];
  if (special) return special;

  if (key.length === 1) {
    return key.toUpperCase();
  }

  return key;
}

function getKeyFromDisplayName(displayName: string, shift: boolean): string {
  if (REVERSE_SPECIAL_KEY_DISPLAY[displayName]) {
    return REVERSE_SPECIAL_KEY_DISPLAY[displayName];
  }

  if (shift) {
    const shifted = SHIFTED_KEY_MAP[displayName];
    if (shifted) {
      return shifted;
    }
  }

  if (displayName.length === 1) {
    return displayName.toLowerCase();
  }

  return displayName;
}

export function serializeKeyCombination(combo: KeyCombination): string {
  const normalized = normalizeCombination(combo);
  const parts: string[] = [];

  if (normalized.primary) parts.push('Primary');
  if (normalized.alt) parts.push('Alt');
  if (normalized.shift) parts.push('Shift');

  parts.push(getKeyDisplayName(normalized));

  return parts.join('+');
}

export function keyCombinationDisplayParts(
  combo: KeyCombination,
  platform: PlatformFlavor = detectPlatform(),
): string[] {
  const normalized = normalizeCombination(combo);
  const parts: string[] = [];

  if (normalized.primary) {
    parts.push(platform === 'mac' ? 'Cmd' : 'Ctrl');
  }

  if (normalized.alt) {
    parts.push(platform === 'mac' ? 'Option' : 'Alt');
  }

  if (normalized.shift) {
    parts.push('Shift');
  }

  parts.push(getKeyDisplayName(normalized));

  return parts;
}

export function formatKeyCombination(
  combo: KeyCombination,
  platform: PlatformFlavor = detectPlatform(),
): string {
  return keyCombinationDisplayParts(combo, platform).join('+');
}

export const keyCombinationToString = formatKeyCombination;

export function stringToKeyCombination(str: string): KeyCombination | null {
  const parts = str.split('+');
  if (parts.length === 0) return null;

  const combo: KeyCombination = { key: '' };

  for (let i = 0; i < parts.length - 1; i++) {
    const modifier = parts[i].toLowerCase();
    switch (modifier) {
      case 'primary':
      case 'ctrl':
      case 'control':
      case 'cmd':
      case 'command':
      case '⌘':
        combo.primary = true;
        break;
      case 'alt':
      case 'option':
        combo.alt = true;
        break;
      case 'shift':
      case '⇧':
        combo.shift = true;
        break;
    }
  }

  const shift = !!combo.shift;
  combo.key = getKeyFromDisplayName(parts[parts.length - 1], shift);

  return normalizeCombination(combo);
}

export function eventToKeyCombination(event: KeyboardEvent): KeyCombination {
  return normalizeCombination({
    key: event.key,
    primary: event.ctrlKey || event.metaKey,
    alt: event.altKey,
    shift: event.shiftKey,
  });
}

export function createDefaultKeyMapEntries(
  _platform: PlatformFlavor = detectPlatform(),
): Array<[KeyActions, KeyCombination]> {
  const entries: Array<[KeyActions, KeyCombination]> = [
    [KeyActions.JUMP_TO_COORDINATES, { key: 'g', primary: true }],
    [KeyActions.MOVE_UP, { key: 'ArrowUp' }],
    [KeyActions.MOVE_DOWN, { key: 'ArrowDown' }],
    [KeyActions.MOVE_LEFT, { key: 'ArrowLeft' }],
    [KeyActions.MOVE_RIGHT, { key: 'ArrowRight' }],
    [KeyActions.CENTER, { key: 'h' }],
    [KeyActions.ZOOM_IN, { key: '=' }],
    [KeyActions.ZOOM_OUT, { key: '-' }],
    [KeyActions.ZOOM_RESET, { key: '0' }],
    [KeyActions.PAN_TOOL, { key: 'p' }],
    [KeyActions.SELECT_TOOL, { key: 's' }],
    [KeyActions.TILE_TOOL, { key: 't' }],
    [KeyActions.CHIP_TOOL, { key: 'c' }],
    [KeyActions.DELETE_TOOL, { key: 'x' }],
    [KeyActions.DEBUG_TOOL, { key: 'd' }],
    [KeyActions.UNDO, { key: 'z', primary: true }],
    [KeyActions.REDO, { key: 'z', shift: true, primary: true }],
    [KeyActions.ROTATE, { key: 'r' }],
    [KeyActions.ROTATE_BACKWARDS, { key: 'r', shift: true }],
    [KeyActions.SETTINGS, { key: ',', shift: true }],
    [KeyActions.COPY, { key: 'c', primary: true }],
    [KeyActions.PASTE, { key: 'v', primary: true }],
    [KeyActions.CUT, { key: 'x', primary: true }],
    [KeyActions.DELETE_SELECTION, { key: 'Backspace' }],
    [KeyActions.ESCAPE, { key: 'Escape' }],
  ];

  return entries.map(([action, combo]) => [action, normalizeCombination(combo)]);
}

export function createDefaultKeyMap(
  platform: PlatformFlavor = detectPlatform(),
): Map<KeyActions, KeyCombination> {
  return new Map(createDefaultKeyMapEntries(platform));
}

export const defaultKeyMap = createDefaultKeyMap();

function buildBiDirectionalMap(entries: Array<[KeyActions, KeyCombination]>) {
  return makeAutoObservable(
    new BiDirectionalMap<KeyActions, string>(
      entries.map(([action, combo]) => [action, serializeKeyCombination(combo)]),
    ),
  );
}

export class KeyMapModel implements IKeyMapModel {
  keyMap: BiDirectionalMap<KeyActions, string>;
  tileBindings = new Map<TileShortcutKey, TileShortcutTarget>();

  getTileShortcut(combo: KeyCombination): TileShortcutTarget | undefined {
    if (combo.primary || combo.alt || combo.shift || !isTileShortcutKey(combo.key))
      return;
    // App shortcuts always win, including ones supplied by extensions.
    if (this.getKeyAction(combo)) return;
    return this.tileBindings.get(combo.key);
  }

  getTileShortcutKey(templateId: string): TileShortcutKey | undefined {
    return [...this.tileBindings].find(
      ([, target]) => target.templateId === templateId,
    )?.[0];
  }

  setTileShortcut(
    key: TileShortcutKey,
    target: TileShortcutTarget,
    replace = false,
  ): boolean {
    if (!isTileShortcutKey(key) || this.getKeyAction({ key })) return false;
    const existing = this.tileBindings.get(key);
    if (existing && existing.templateId !== target.templateId && !replace) return false;
    this.clearTileShortcut(target.templateId);
    this.tileBindings.set(key, { ...target });
    return true;
  }

  clearTileShortcut(templateId: string): void {
    const key = this.getTileShortcutKey(templateId);
    if (key) this.tileBindings.delete(key);
  }

  private readonly platform: PlatformFlavor;

  constructor(options?: { platformOverride?: PlatformFlavor }) {
    this.platform = options?.platformOverride ?? detectPlatform();
    this.keyMap = buildBiDirectionalMap(createDefaultKeyMapEntries(this.platform));
    makeAutoObservable(this, {}, { autoBind: true });
  }

  setChar(key: KeyActions, value: KeyCombination): void {
    const tile = this.getTileShortcut(value);
    if (tile)
      throw new Error(
        `This key is assigned to ${tile.name}. Clear its tile shortcut first.`,
      );
    this.keyMap.set(key, serializeKeyCombination(value));
  }

  getChar(key: KeyActions): KeyCombination | undefined {
    const str = this.keyMap.getValue(key);
    return str ? stringToKeyCombination(str) || undefined : undefined;
  }

  getKeyAction(combo: KeyCombination): KeyActions | undefined {
    return this.keyMap.getKey(serializeKeyCombination(combo));
  }

  // Legacy methods for backward compatibility
  getCharString(key: KeyActions): string | undefined {
    const combo = this.getChar(key);
    return combo ? formatKeyCombination(combo, this.platform) : undefined;
  }

  setCharFromString(key: KeyActions, value: string): void {
    const combo = stringToKeyCombination(value);
    if (combo) {
      this.setChar(key, combo);
    }
  }

  getKeyActionFromString(str: string): KeyActions | undefined {
    const combo = stringToKeyCombination(str);
    return combo ? this.getKeyAction(combo) : undefined;
  }

  resetToDefaults(): void {
    this.keyMap = buildBiDirectionalMap(createDefaultKeyMapEntries(this.platform));
  }
}
